import React, { useEffect, useState } from "react";
import BookDonationManagerService from "../../services/dashboard/BookDonationManager";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FaFileCsv, FaFileExcel } from "react-icons/fa";

const BookDonationManager = () => {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionMessage, setActionMessage] = useState("");

  useEffect(() => {
    fetchDonations();
  }, []);

  const fetchDonations = async () => {
    try {
      const data = await BookDonationManagerService.getAllDonations();
      setDonations(data || []);
    } catch (error) {
      console.error("Failed to fetch donations:", error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 1. Map the raw donation objects into a flat array of plain objects
   *    with exactly the columns you want to export.
   */
  const mapDonationsForExport = () => {
    return donations.map((d) => ({
      ID: d.bdId,
      Name: `${d.firstName || ""} ${d.lastName || ""}`.trim(),
      Email: d.email || "",
      Contact: d.contact || "",
      Address: d.address || "",
      City: d.city || "",
      Pincode: d.pincode || "",
      "Donation Date": d.donationDate || "",
      "Created At": d.creationDate
        ? new Date(d.creationDate).toLocaleString()
        : "",
    }));
  };

  /**
   * 2. Export to CSV:
   *    - Convert JSON → worksheet, then sheet → CSV text
   *    - Wrap CSV string in a Blob (“text/csv”) and trigger download
   */
  const handleDownloadCSV = () => {
    const exportData = mapDonationsForExport();
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Donations");

    // Generate CSV content
    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "book_donations.csv");
  };

  /**
   * 3. Export to Excel (.xlsx):
   *    - Convert sheet → XLSX binary, then wrap in Blob and trigger download
   */
  const handleDownloadExcel = () => {
    const exportData = mapDonationsForExport();
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Donations");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob(
      [excelBuffer],
      {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }
    );
    saveAs(blob, "book_donations.xlsx");
  };

  const formatDateFromArray = ([year, month, day]) => {
    if (!year || !month || !day) return "";
    const date = new Date(year, month - 1, day); // JS months are 0-indexed
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }); // returns like "20 Apr 2025"
  };


  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-[1800px] mx-auto p-6">
        {/* Header Section */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-8">
          {/* Title + Description */}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
              Book Donations
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Manage and track book donation requests
            </p>
          </div>

          {/* Export Buttons - Enhanced Hover */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadCSV}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-150 ease-out shadow-sm hover:shadow-md hover:-translate-y-0.5"
            >
              <FaFileCsv className="w-4 h-4" />
              Download CSV
            </button>
            <button
              onClick={handleDownloadExcel}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-150 ease-out shadow-sm hover:shadow-md hover:-translate-y-0.5"
            >
              <FaFileExcel className="w-4 h-4" />
              Download Excel
            </button>
          </div>
        </div>

        {/* Action Message */}
        {actionMessage && (
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-blue-800 dark:text-blue-300 text-sm font-medium">{actionMessage}</p>
          </div>
        )}

        {/* Elite Table Container with Custom Scrollbar */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400 text-lg animate-pulse">Loading donations...</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500">
              <style>{`
                .scrollbar-thin::-webkit-scrollbar {
                  height: 8px;
                }
                .scrollbar-thin::-webkit-scrollbar-track {
                  background: transparent;
                }
                .scrollbar-thin::-webkit-scrollbar-thumb {
                  background-color: rgb(209 213 219);
                  border-radius: 4px;
                }
                .dark .scrollbar-thin::-webkit-scrollbar-thumb {
                  background-color: rgb(75 85 99);
                }
                .scrollbar-thin:hover::-webkit-scrollbar-thumb {
                  background-color: rgb(156 163 175);
                }
                .dark .scrollbar-thin:hover::-webkit-scrollbar-thumb {
                  background-color: rgb(107 114 128);
                }
              `}</style>
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                {/* Light Gray Header - Reduced Padding */}
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">ID</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Name</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Email</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Contact</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Address</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">City</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Pincode</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Donation Date</th>
                  </tr>
                </thead>
                
                {/* Elite Polished Table Body - Final Polish */}
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50 bg-white dark:bg-gray-800">
                  {donations.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-12 text-gray-500 dark:text-gray-400">
                        No donations found.
                      </td>
                    </tr>
                  ) : (
                    donations.map((donation, index) => (
                      <tr
                        key={donation.bdId}
                        className={`transition-all duration-150 ease-out hover:bg-gray-100/60 dark:hover:bg-gray-900/60 cursor-pointer ${
                          index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/40 dark:bg-gray-900/20'
                        }`}
                      >
                        <td className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">{donation.bdId}</td>
                        <td className="px-3 py-3 text-sm font-semibold text-gray-950 dark:text-white">
                          {donation.firstName} {donation.lastName}
                        </td>
                        <td className="px-3 py-3 text-xs text-gray-500 dark:text-gray-400">{donation.email}</td>
                        <td className="px-3 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">{donation.contact}</td>
                        <td className="px-3 py-3 max-w-xs">
                          <div 
                            className="truncate text-sm text-gray-600 dark:text-gray-400"
                            title={donation.address}
                          >
                            {donation.address}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400">{donation.city}</td>
                        <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400">{donation.pincode}</td>
                        <td className="px-3 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {donation.donationDate ? formatDateFromArray(donation.donationDate) : "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookDonationManager;
